// -----------------------------------------------------------------------------
// DebtDialog.jsx  — 2025‑05‑07  (FULL component, cleaned & self‑contained)
// -----------------------------------------------------------------------------

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

/* ─────────────────────────── CONSTANTS ─────────────────────────── */

const INSTRUMENT_TYPES = [
  "cash_on_hand",
  "credit_card",
  "line_of_credit",
  "debt_equity",
  "life_insurance_loan",
  "other",
];

const PAYMENT_FREQUENCIES = [
  "monthly",
  "quarterly",
  "annual",
  "one_time",
  "on_demand",
  "other",
];

const STATUSES = ["active", "pending", "paid"];

/* numeric fields we should cast → number  (empty → omit) */
const NUMERIC_FIELDS = [
  "company_id",
  "credit_limit",
  "balance",
  "interest_rate",
  "payment_amount",
];

/* Helper – strip empty strings & cast numerics */
const cleanPayload = (raw) => {
  const out = {};
  Object.entries(raw).forEach(([k, v]) => {
    if (v === "" || v === null || v === undefined) return;        // drop empties
    if (NUMERIC_FIELDS.includes(k)) {
      const num = Number(v);
      if (!Number.isNaN(num)) out[k] = num;
      return;
    }
    out[k] = v;                                                   // plain copy
  });
  return out;
};

/* -------------------------------------------------------------------------- */
/*                                 DebtForm                                   */
/* -------------------------------------------------------------------------- */
const DebtForm = ({ initialValues, onSave, onCancel }) => {
  const [form, setForm] = useState(initialValues);
  const isEdit = Boolean(initialValues?.id);

  /* keep state in‑sync if parent swaps initialValues */
  useEffect(() => setForm(initialValues), [initialValues]);

  const handleChange = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = () => onSave(cleanPayload(form));

  return (
    <>
      <DialogContent dividers>
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr" }}>
          {/* Instrument type */}
          <FormControl fullWidth required>
            <InputLabel>Instrument Type</InputLabel>
            <Select
              label="Instrument Type"
              value={form.instrument_type}
              onChange={handleChange("instrument_type")}
            >
              {INSTRUMENT_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status */}
          <FormControl fullWidth required>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={form.status} onChange={handleChange("status")}>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Provider */}
          <TextField
            label="Provider"
            value={form.provider}
            onChange={handleChange("provider")}
            required
            fullWidth
          />

          {/* Company (optional) */}
          <TextField
            label="Company ID (optional)"
            value={form.company_id ?? ""}
            onChange={handleChange("company_id")}
            type="number"
            fullWidth
          />

          {/* Credit limit / balance */}
          <TextField
            label="Credit Limit"
            value={form.credit_limit ?? ""}
            onChange={handleChange("credit_limit")}
            type="number"
            fullWidth
          />
          <TextField
            label="Balance"
            value={form.balance ?? ""}
            onChange={handleChange("balance")}
            type="number"
            fullWidth
          />

          {/* Interest / maturity */}
          <TextField
            label="Interest Rate (%)"
            value={form.interest_rate ?? ""}
            onChange={handleChange("interest_rate")}
            type="number"
            fullWidth
          />
          <TextField
            label="Maturity Date"
            value={form.maturity_date ?? ""}
            onChange={handleChange("maturity_date")}
            type="date"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          {/* Payment schedule */}
          <FormControl fullWidth>
            <InputLabel>Payment Frequency</InputLabel>
            <Select
              label="Payment Frequency"
              value={form.payment_frequency ?? ""}
              onChange={handleChange("payment_frequency")}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {PAYMENT_FREQUENCIES.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Payment Amount"
            value={form.payment_amount ?? ""}
            onChange={handleChange("payment_amount")}
            type="number"
            fullWidth
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!form.instrument_type || !form.provider}
          onClick={handleSubmit}
        >
          {isEdit ? "Save Changes" : "Create"}
        </Button>
      </DialogActions>
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*                               DebtDialog Shell                             */
/* -------------------------------------------------------------------------- */
const DebtDialog = ({ open, instruments = [], onCreate, onUpdate, onClose }) => {
  const [mode, setMode] = useState("list");     // list | add | edit
  const [editing, setEditing] = useState(null); // object being edited

  const reset = () => {
    setMode("list");
    setEditing(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  /* --- submit handlers ---------------------------------------------------- */
  const handleCreate = (payload) => {
    onCreate(payload);
    reset();
  };

  const handleUpdate = (payload) => {
    onUpdate({ id: editing.id, payload });
    reset();
  };

  /* ============= ADD / EDIT SCREEN ============= */
  if (mode !== "list") {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>{mode === "add" ? "Add Debt Instrument" : "Edit Debt Instrument"}</DialogTitle>
        <DebtForm
          initialValues={
            mode === "edit"
              ? editing
              : {
                  instrument_type: "",
                  status: "active",
                  provider: "",
                  credit_limit: "",
                  balance: "",
                  interest_rate: "",
                  maturity_date: "",
                  payment_frequency: "",
                  payment_amount: "",
                  company_id: "",
                }
          }
          onSave={mode === "add" ? handleCreate : handleUpdate}
          onCancel={() => setMode("list")}
        />
      </Dialog>
    );
  }

  /* ================= LIST SCREEN ================= */
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        Debt Instruments
        <IconButton sx={{ ml: 1 }} size="small" color="primary" onClick={() => setMode("add")}>
          <Tooltip title="Add new">
            <AddIcon fontSize="small" />
          </Tooltip>
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {instruments.length === 0 ? (
          <Typography variant="body2">No instruments yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell align="right">Limit</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="right">Rate %</TableCell>
                <TableCell>Maturity</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {instruments.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>{d.id}</TableCell>
                  <TableCell>{d.instrument_type}</TableCell>
                  <TableCell>{d.status}</TableCell>
                  <TableCell>{d.provider}</TableCell>
                  <TableCell align="right">{d.credit_limit ?? "-"}</TableCell>
                  <TableCell align="right">{d.balance}</TableCell>
                  <TableCell align="right">{d.interest_rate ?? "-"}</TableCell>
                  <TableCell>{d.maturity_date ? d.maturity_date.split("T")[0] : "-"}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditing(d);
                        setMode("edit");
                      }}
                    >
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DebtDialog;
